import cx from "classnames";
import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useUserContext } from "../../../../contexts/UserContext";
import {
  DEFAULT_COLUMNS,
  MIN_COLUMNS,
  selectColumnsCount,
  setColumnCount,
} from "../../../../redux/slicers/admin/curatorLayoutSlicer";
import { CURATOR_MENU_SECTION } from "../../../../redux/slicers/admin/curatorMenuPreferencesSlicer";
import {
  curatorActions,
  curatorSelector,
  selectedIsImageScanSearchFile,
  selecteIsFilterOpen,
  selectSidebarExpanded,
  selectSidebarSection,
  setSidebarExpanded,
  toggleSidebarExpanded,
  toggleSidebarSection,
} from "../../../../redux/slicers/admin/curatorSlicer";
import {
  selectActiveTab,
  selectIsSelectMapModeActive,
  TAB_NAME,
} from "../../../../redux/slicers/admin/curatorStylesSlicer";
import { selectGamificationTooltipData } from "../../../../redux/slicers/gamificationTooltipSlicer";
import { getAppState } from "../../../../redux/store";
import {
  isUserOnboarding,
  setTooltipData,
} from "../../../gamificationTooltip/helpers";
import ArrowDownIcon from "../../../icons/ArrowDownIcon";
import styles from "./CuratorSidebarLayout.module.scss";
import { LogMixPanel, Mixpanel, MxEvent } from "../../../../common/Mixpanel";

const useExpandedData = () => {
  const sidebarActiveSection = useSelector(selectSidebarSection);
  const styleActiveTab = useSelector(selectActiveTab);
  const isSelectMapModeActive = useSelector(selectIsSelectMapModeActive);
  const columnsCount = useSelector((state) =>
    selectColumnsCount(state, sidebarActiveSection)
  );

  const isProductsSection =
    sidebarActiveSection === CURATOR_MENU_SECTION.PRODUCTS;
  const isStyleTextures =
    sidebarActiveSection === CURATOR_MENU_SECTION.STYLE &&
    styleActiveTab === TAB_NAME.TEXTURES;
  const isStyleMaterials =
    sidebarActiveSection === CURATOR_MENU_SECTION.STYLE &&
    styleActiveTab === TAB_NAME.MATERIALS;
  const isCustomMapSelection =
    sidebarActiveSection === CURATOR_MENU_SECTION.STYLE &&
    styleActiveTab === TAB_NAME.PROPERTIES &&
    isSelectMapModeActive;
  const isExpandableSection =
    isProductsSection ||
    isStyleTextures ||
    isStyleMaterials ||
    isCustomMapSelection;

  return {
    isExpandableSection,
    columnsCount: isExpandableSection ? columnsCount : DEFAULT_COLUMNS,
  };
};

export const CuratorSidebarLayout = ({
  sectionName,
  inside,
  className,
  fixed,
  style = {},
  ...rest
}) => {
  // const expanded = useSelector((state) => curatorSelector(state).sidebarExpanded);
  const { columnsCount } = useExpandedData();
  const isOnboardingState = isUserOnboarding();

  return (
    <div
      {...rest}
      id={`curatorSidebarLayout__${sectionName}`}
      style={{
        width: calculateSidebarWidth(columnsCount),
        ...style,
      }}
      className={cx(
        styles.root,
        isOnboardingState && styles.zIndexMax,
        className,
        // expanded && styles.expanded,
        inside && styles.inside,
        fixed && styles.fixed
      )}
    ></div>
  );
};

CuratorSidebarLayout.Inner = (props) => {
  return (
    <div
      {...props}
      className={cx(styles.inner, props.className)}
      id={`CuratorSidebarLayoutScrollableContainer_${props.name}`}
    />
  );
};

CuratorSidebarLayout.FixedHeader = (props) => {
  return (
    <div
      id="sidebarFixedHeader"
      {...props}
      className={cx(styles.fixedHeader, props.className)}
    ></div>
  );
};

CuratorSidebarLayout.FixedFooter = (props) => {
  return (
    <div
      id="sidebarFixedFooter"
      {...props}
      className={cx(styles.fixedFooter, props.className)}
    ></div>
  );
};

CuratorSidebarLayout.Filter = function Filter({ className, ...rest }) {
  // const expanded = useSelector((state) => curatorSelector(state).sidebarExpanded);
  const activeSection = useSelector(selectSidebarSection);
  const columnsCount = useSelector((state) =>
    selectColumnsCount(state, activeSection)
  );

  const sidebarWidth = calculateSidebarWidth(columnsCount);
  return (
    <div
      {...rest}
      id="curatorSidebarLayoutFilter"
      style={{
        transform: `translateX(${sidebarWidth - defaultSidebarWidth}px)`,
        width: filterWidth,
      }}
      className={cx(
        styles.filter,
        className
        // expanded && styles.filterExpanded
      )}
    ></div>
  );
};

CuratorSidebarLayout.Button = function Button({ location, ...rest }) {
  const dispatch = useDispatch();
  const filterOpen = useSelector(selecteIsFilterOpen);
  const { isExpandableSection } = useExpandedData();
  const { currentUser } = useUserContext();
  const gamificationTooltipData = useSelector(selectGamificationTooltipData);
  const { step } = gamificationTooltipData;
  const isOnboardingState = isUserOnboarding();
  const imageSearchFile = useSelector(selectedIsImageScanSearchFile);
  const disableClose = step !== "CLICK_ARROW" && isOnboardingState || rest?.disabled;
  const {
    handleExpandClick,
    handleCollapseClick,
    handleMouseDown,
    handleMouseUp,
  } = useSidebarResize();

  if (filterOpen && location === "sidebar") return null;

  const handleButtonClick = () => {
    if (isExpandableSection) return;
    dispatch(toggleSidebarSection(null));
  };

  const handleGamifyClose = () => {
    const isMaterialApplied =
      currentUser?.user?.other_info?.gamifyOnBoarding?.data
        ?.selectedOnboardingOtherMaterial?.id;
    if (step === "CLICK_ARROW" && isMaterialApplied) {
      setTooltipData({
        step: "CLICK_AND_DRAG_TO_ROTATE",
      });
    }
  };

  if (!isExpandableSection) {
    return (
      <button
        type="button"
        id="curatorSidebarExpandCollapse"
        {...rest}
        className={cx(styles.button, location === 'filter' && styles.butonFilterLocation)}
        onClick={() => {
          if (disableClose) {
            return;
          }
          handleButtonClick();
        }}>
        <ArrowDownIcon
          className={cx(styles.imgCollapse)}
          onClick={handleCollapseClick}
        />
      </button>
    );
  }

  return (
    <button
      id="curatorSidebarExpandCollapse"
      type="button"
      ref={(elem) => {
        if (elem && isOnboardingState && step === "CLICK_ARROW") {
          setTooltipData({
            target: elem,
          });
        }
      }}
      disabled={disableClose} // step !== "CLICK_ARROW" && isOnboardingState}
      {...rest}
      className={cx(
        styles.button,
        location === "filter" && styles.butonFilterLocation
      )}
      onClick={() => {
        if (disableClose) {
          return;
        }
        handleButtonClick();
      }}
    >
      <ArrowDownIcon
        className={cx(
          styles.imgExpand,
          isOnboardingState && step === "CLICK_ARROW" && "blurInput"
        )}
        onClick={() => {
          if (imageSearchFile) {
            dispatch(toggleSidebarSection(null));
            return;
          }
          if (disableClose) {
            return;
          }
          handleExpandClick();
        }}
        onMouseDown={() => {
          if (!isOnboardingState && !imageSearchFile) {
            handleMouseDown();
          }
        }}
        onMouseUp={() => {
          if (!isOnboardingState && !imageSearchFile) {
            handleMouseDown();
          }
        }}
      />
      <ArrowDownIcon
        className={cx(styles.imgCollapse, styles.translated)}
        onClick={() => {
          if (imageSearchFile) {
            dispatch(toggleSidebarSection(null));
            return;
          }
          if (!isOnboardingState) {
            handleCollapseClick();
            return;
          }
          handleGamifyClose();
        }}
        onMouseDown={() => {
          if (!isOnboardingState && !imageSearchFile) {
            handleMouseDown();
          }
        }}
        onMouseUp={() => {
          if (!isOnboardingState && !imageSearchFile) {
            handleMouseDown();
          }
        }}
      />
    </button>
  );
};

const minWidth = 416;
const spacing = 8;
const cardWidth = 188;
const padding = 24; // padding 32 - one card padding
const cardWithSpacing = cardWidth + spacing;
const sidebarWidth = 96; // TODO: calculate it
const filterWidth = 231; // TODO: calculate it
const defaultSidebarWidth = padding + 2 * cardWithSpacing;

const calculateSidebarWidth = (columnsCount) => {
  return padding + columnsCount * cardWithSpacing;
};

export const useSidebarResize = () => {
  const dispatch = useDispatch();
  const activeSection = useSelector(selectSidebarSection);
  const resizeRef = useRef({
    resized: false,
    mouseDown: false,
    initialClientX: null,
    sidebarElement: null,
    initialSidebarWidth: null,
    maxWidth: 1000,
    filter: null,
  });

  const getMaxWidth = () => {
    const availableSpace = window.innerWidth - sidebarWidth - filterWidth - 50; // 50 for spacing
    const columnsCount = Math.floor(
      (availableSpace - padding) / cardWithSpacing
    );
    const maxWidth = padding + columnsCount * cardWithSpacing;
    return { maxWidth, columnsCount };
  };

  const handlePanelResizeMixPanelEvent = (size) => {
    if(size<2){
      Mixpanel.track( true, MxEvent.CURATOR_PANEL_CLOSED)
    }else{
      Mixpanel.track( true, MxEvent.CURATOR_PANEL_RESIZED,{"Columns visible": size})
    }
  }

  const handleExpandClick = () => {
    if (resizeRef.current.resized) return;
    const columnsCount = selectColumnsCount(getAppState(), activeSection);
    const maxColumnsCount = getMaxWidth().columnsCount;
    const newColumnsCount = columnsCount + 1;

    if (newColumnsCount <= maxColumnsCount) {
      handlePanelResizeMixPanelEvent(newColumnsCount);
      dispatch(
        setColumnCount({
          sectionName: activeSection,
          columnsCount: newColumnsCount,
        })
      );
    }
  };
  const handleCollapseClick = () => {
    if (resizeRef.current.resized) return;
    const columnsCount = selectColumnsCount(getAppState(), activeSection);
    const newColumnsCount = columnsCount - 1;

    if (newColumnsCount >= MIN_COLUMNS) {
      handlePanelResizeMixPanelEvent(newColumnsCount);
      dispatch(
        setColumnCount({
          sectionName: activeSection,
          columnsCount: newColumnsCount,
        })
      );
    } else {
      handlePanelResizeMixPanelEvent(newColumnsCount);
      dispatch(toggleSidebarSection(null));
    }
  };

  const updateFilterPosition = (width) => {
    const { filter } = resizeRef.current;
    if (!filter) return;
    const translate = width - defaultSidebarWidth;
    filter.style.transform = `translateX(${translate}px)`;
  };

  const handleMouseMove = (e) => {
    const {
      initialClientX,
      mouseDown,
      sidebarElement,
      initialSidebarWidth,
      maxWidth,
    } = resizeRef.current;
    if (!mouseDown) return;

    // I added below condition because once the user reach max width then drag feature not working because of width update more then max width.
    // const delta = e.clientX - initialClientX;
    // let width = initialSidebarWidth + delta;
    let width = e.clientX;

    if (width < minWidth) {
      width = minWidth;
    }

    if (width > maxWidth) {
      width = maxWidth;
    }

    sidebarElement.style.width = `${width}px`;
    updateFilterPosition(width);
    resizeRef.current.resized = true;
  };

  const handleMouseUp = (e) => {
    const { sidebarElement, maxWidth } = resizeRef.current;

    resizeRef.current.mouseDown = false;
    window.removeEventListener("mousemove", handleMouseMove);
    window.removeEventListener("mouseup", handleMouseUp);

    // TODO: calculate width
    const currentWidth = sidebarElement.clientWidth;
    const contentWidth = currentWidth - padding;
    let columnsCount = Math.round(contentWidth / cardWithSpacing);
    let width = calculateSidebarWidth(columnsCount);

    if (width > maxWidth) {
      // minus one is enough as we don't allow to resize more
      width = width - cardWithSpacing;
      columnsCount -= 1;
    }

    if (width < minWidth) {
      width = minWidth;
      columnsCount += 1;
    }

    width = calculateSidebarWidth(columnsCount);
    sidebarElement.style.width = `${width}px`;
    updateFilterPosition(width);
    dispatch(
      setColumnCount({
        sectionName: activeSection,
        columnsCount,
      })
    );
  };

  const handleMouseDown = (e) => {
    const sectionId = `curatorSidebarLayout__${activeSection}`;
    resizeRef.current.mouseDown = true;
    resizeRef.current.resized = false;
    resizeRef.current.initialClientX = e?.clientX || '';
    resizeRef.current.sidebarElement = document.getElementById(sectionId);
    resizeRef.current.initialSidebarWidth = document.getElementById(
      sectionId
    ).clientWidth;
    resizeRef.current.filter = document.getElementById(
      "curatorSidebarLayoutFilter"
    );

    // calculate max width
    resizeRef.current.maxWidth = getMaxWidth().maxWidth;

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
  };

  const handleResetSlider = () => {
    let columnsCount = 2;
    const {
      sidebarElement,
    } = resizeRef.current;
    if(sidebarElement?.style) sidebarElement.style.width = `${minWidth}px`;
    updateFilterPosition(minWidth);
    dispatch(
      setColumnCount({
        sectionName: activeSection,
        columnsCount,
      })
    );
  }

  return {
    handleExpandClick,
    handleCollapseClick,
    handleMouseDown,
    handleMouseUp,
    handleMouseMove,
    handleResetSlider,
  };
};
